"use client";

import { useCallback } from "react";
import {
  clearCheckpoint as clearStoredCheckpoint,
  deriveRemainingCheckIds as deriveCheckpointRemainingCheckIds,
  getCheckpoint as getStoredCheckpoint,
  saveCheckpoint as saveStoredCheckpoint,
  updateCheckpoint,
} from "@/lib/aicheck/checkpoint-storage";
import type {
  AICheckResult,
  AICheckRunCheckpoint,
  CheckLane,
  ConsistencyCheckResult,
  ContactTopicsResult,
  CustomerProfileResult,
  FailedCheckInfo,
  FailedStage,
  NodeExecutionDirective,
  OutputLanguage,
  PipelineGraphSkeleton,
  PipelineNodeMembers,
  PipelineNodeRuntimeStatus,
  RunStatus,
  ScheduledAdminControl,
  SkippedItem,
} from "@/types/aicheck";

function buildFreshCheckpoint(
  projectId: number,
  language: OutputLanguage,
  startedAt: string,
): AICheckRunCheckpoint {
  return {
    projectId,
    language,
    startedAt,
    updatedAt: new Date().toISOString(),
    completedCheckIds: [],
    aiResults: [],
    consistencyResults: [],
  };
}

export function useRunCheckpoint() {
  const getCheckpoint = useCallback(getStoredCheckpoint, []);

  const clearCheckpoint = useCallback(
    (projectId: number, language: OutputLanguage) => {
      clearStoredCheckpoint(projectId, language);
    },
    [],
  );

  const replaceCheckpoint = useCallback(
    (checkpoint: AICheckRunCheckpoint) => saveStoredCheckpoint(checkpoint),
    [],
  );

  const ensureStarted = useCallback(
    (projectId: number, language: OutputLanguage, startedAt: string) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) =>
        current.startedAt === startedAt
          ? current
          : buildFreshCheckpoint(projectId, language, startedAt),
      ),
    [],
  );

  const setRunStatus = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      status: RunStatus,
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => ({
        ...current,
        lastKnownStatus: status,
      })),
    [],
  );

  const setProjectData = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      projectData: AICheckRunCheckpoint["projectDataSnapshot"],
      projectDataSource?: AICheckRunCheckpoint["projectDataSource"],
      signature?: string,
      signatureKid?: string,
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => ({
        ...current,
        signatureKid: signatureKid ?? current.signatureKid,
        projectDataSnapshot: projectData,
        projectDataSource: projectDataSource ?? current.projectDataSource,
        projectDataSignature: signature,
      })),
    [],
  );

  const addPlannedChecks = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      checks: string[],
      lane?: CheckLane,
      checkIds?: string[],
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => {
        const plannedChecks = [...(current.plannedCheckIds ?? [])];
        const plannedCheckNameById = {
          ...(current.plannedCheckNameById ?? {}),
        };
        const knownIds = new Set(plannedChecks);
        for (let i = 0; i < checks.length; i += 1) {
          const checkId = checkIds?.[i];
          const checkName = checks[i];
          if (checkId) {
            plannedCheckNameById[checkId] = checkName;
          }
          if (checkId && !knownIds.has(checkId)) {
            knownIds.add(checkId);
            plannedChecks.push(checkId);
          }
        }
        const nextCheckLaneMap = lane
          ? {
              ...(current.checkLaneMap ?? {}),
              ...Object.fromEntries(
                checks.map((checkName) => [checkName, lane]),
              ),
            }
          : current.checkLaneMap;
        return {
          ...current,
          plannedCheckIds: plannedChecks,
          plannedCheckNameById,
          checkLaneMap: nextCheckLaneMap,
        };
      }),
    [],
  );

  const setCheckBatchGroups = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      checkBatchGroups: Array<{ id: string; name: string; checks: string[] }>,
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => ({
        ...current,
        checkBatchGroups,
      })),
    [],
  );

  const setGraphSkeleton = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      graphSkeleton: PipelineGraphSkeleton,
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => ({
        ...current,
        graphSkeleton,
      })),
    [],
  );

  const setNodeMembers = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      nodeMembers: PipelineNodeMembers,
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => ({
        ...current,
        nodeMembers,
      })),
    [],
  );

  const setNodeExecutionState = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      snapshot: {
        nodeStates?: Record<string, PipelineNodeRuntimeStatus>;
        nodeExecutionDirectives?: NodeExecutionDirective[];
        nodeControlVersion?: number;
      },
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => ({
        ...current,
        nodeStates: snapshot.nodeStates ?? current.nodeStates,
        nodeExecutionDirectives:
          snapshot.nodeExecutionDirectives ?? current.nodeExecutionDirectives,
        nodeControlVersion:
          snapshot.nodeControlVersion ?? current.nodeControlVersion,
      })),
    [],
  );

  const appendAIResult = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      result: AICheckResult,
      signature?: string,
      signatureKid?: string,
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => ({
        ...current,
        signatureKid: signatureKid ?? current.signatureKid,
        aiResults: [
          ...(current.aiResults ?? []).filter(
            (entry) => entry.result.checkId !== result.checkId,
          ),
          { result, signature },
        ],
      })),
    [],
  );

  const mergeConsistencyResults = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      results: ConsistencyCheckResult[],
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => ({
        ...current,
        consistencyResults: [...(current.consistencyResults ?? []), ...results],
      })),
    [],
  );

  const setProfileResult = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      result: CustomerProfileResult | null,
      signature?: string,
      signatureKid?: string,
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => ({
        ...current,
        signatureKid: signatureKid ?? current.signatureKid,
        customerProfileResult: {
          data: result,
          signature,
        },
      })),
    [],
  );

  const setContactTopicsResult = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      result: ContactTopicsResult | null,
      signature?: string,
      signatureKid?: string,
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => ({
        ...current,
        signatureKid: signatureKid ?? current.signatureKid,
        contactTopicsResult: {
          data: result,
          signature,
        },
      })),
    [],
  );

  const setSkippedItems = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      skippedItems: SkippedItem[],
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => ({
        ...current,
        skippedItems: [
          ...(current.skippedItems ?? []),
          ...skippedItems.filter(
            (item) =>
              !(current.skippedItems ?? []).some(
                (existing) => existing.id === item.id,
              ),
          ),
        ],
      })),
    [],
  );

  const syncRunProgress = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      snapshot: {
        lastKnownStatus?: RunStatus;
        failedStage?: FailedStage;
        failedChecks?: FailedCheckInfo[];
        skippedItems?: SkippedItem[];
        nodeStates?: Record<string, PipelineNodeRuntimeStatus>;
        nodeExecutionDirectives?: NodeExecutionDirective[];
        nodeControlVersion?: number;
      },
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => ({
        ...current,
        lastKnownStatus: snapshot.lastKnownStatus ?? current.lastKnownStatus,
        failedStage: snapshot.failedStage ?? current.failedStage,
        failedChecks: snapshot.failedChecks ?? current.failedChecks,
        skippedItems: snapshot.skippedItems ?? current.skippedItems,
        nodeStates: snapshot.nodeStates ?? current.nodeStates,
        nodeExecutionDirectives:
          snapshot.nodeExecutionDirectives ?? current.nodeExecutionDirectives,
        nodeControlVersion:
          snapshot.nodeControlVersion ?? current.nodeControlVersion,
      })),
    [],
  );

  const setScheduledAdminControl = useCallback(
    (
      projectId: number,
      language: OutputLanguage,
      startedAt: string,
      scheduledAdminControl: ScheduledAdminControl | null,
    ) =>
      updateCheckpoint({ projectId, language, startedAt }, (current) => ({
        ...current,
        scheduledAdminControl,
      })),
    [],
  );

  const deriveRemainingCheckIds = useCallback(
    (checkpoint: AICheckRunCheckpoint) =>
      deriveCheckpointRemainingCheckIds(checkpoint),
    [],
  );

  return {
    getCheckpoint,
    clearCheckpoint,
    replaceCheckpoint,
    ensureStarted,
    setRunStatus,
    setProjectData,
    addPlannedChecks,
    setCheckBatchGroups,
    setGraphSkeleton,
    setNodeMembers,
    setNodeExecutionState,
    appendAIResult,
    mergeConsistencyResults,
    setProfileResult,
    setContactTopicsResult,
    setSkippedItems,
    syncRunProgress,
    setScheduledAdminControl,
    deriveRemainingCheckIds,
  };
}
